/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Row } from 'antd';

import { RELATED_CHAMPIONSHIP_EVENT_BY_SEX, RELATED_CONTENT_BY_TYPE } from '@constants/relatedContentModules';
import { Checkbox, Spinner, Typography } from '@atoms';
import { ContentModulePicker, SortableArrayField } from '@molecules';
import { hasArrayItems } from '@utils';

import Form from '../Form';

const RelatedContentField = ({
  form,
  fields,
  eventDisciplines = null,
  isEventFetching = false,
  onRelatedEventChange,
}) => {
  const relatedContentFields = React.useMemo(
    () =>
      Object.values(fields).reduce(
        (acc, field) =>
          RELATED_CONTENT_BY_TYPE[field.type]
            ? [...acc, { ...field, relatedContent: RELATED_CONTENT_BY_TYPE[field.type] }]
            : acc,
        [],
      ),
    [fields],
  );

  const relatedContents = React.useMemo(() => relatedContentFields.map((_field) => _field.relatedContent), [
    relatedContentFields,
  ]);

  const handleSubmit = ({ type, content }) => {
    const field = fields.find((_field) => _field.type === type);

    if (type === 'relatedEvents' && onRelatedEventChange) onRelatedEventChange(content);

    const fieldValue = form.getFieldValue(field.name) || [];
    form.setFieldsValue({ [field.name]: [...fieldValue, content] });
  };

  const onDisciplineCheck = React.useCallback(
    ({ target }) => {
      const { value } = target;
      const relatedChampionshipEvents = form?.getFieldValue('relatedChampionshipEvents') || [];

      const triggeredElementInContent = relatedChampionshipEvents?.find(
        (itm) => itm.disciplineId === value.disciplineId && itm.sexCode === value.sexCode,
      );

      const indexOfElement = relatedChampionshipEvents.indexOf(triggeredElementInContent);

      if (indexOfElement > -1) {
        form.setFieldsValue({
          relatedChampionshipEvents: relatedChampionshipEvents
            .map((item, idx) => idx !== indexOfElement && item)
            .filter(Boolean),
        });
      } else form.setFieldsValue({ relatedChampionshipEvents: [...relatedChampionshipEvents, value] });
    },
    [form],
  );

  const RelatedDisciplinesCheckboxes = React.useCallback(() => {
    if (isEventFetching)
      return (
        <div style={{ margin: '20px 0' }}>
          <Spinner />
        </div>
      );
    if (!eventDisciplines || !Object.values(eventDisciplines).some((el) => el.length > 0)) return <></>;

    return (
      <div style={{ marginTop: 20 }}>
        {/* we defenetly should to rewrite this logic */}
        <Row align="middle" justify="space-between">
          <Typography.Title level={4}>Championship Events</Typography.Title>
        </Row>
        <Form.WithFieldValue name="relatedChampionshipEvents">
          {(value) => {
            return Object.keys(eventDisciplines)?.map((key) => {
              const { sexCode } = RELATED_CHAMPIONSHIP_EVENT_BY_SEX[key];
              return (
                <React.Fragment key={key}>
                  <hr color="grey" />
                  <Row align="middle" justify="space-between">
                    <Typography.Title level={4}>{RELATED_CHAMPIONSHIP_EVENT_BY_SEX[key].label}</Typography.Title>
                  </Row>
                  {eventDisciplines[key].map((disc) => (
                    <Checkbox
                      checked={value?.find((itm) => itm.disciplineId === disc.id && itm.sexCode === sexCode)}
                      style={{ marginTop: 10, marginBottom: 10 }}
                      value={{ disciplineId: disc.id, sexCode }}
                      onChange={onDisciplineCheck}
                      key={`${disc.id}-${key}`}
                    >
                      {disc?.name}
                    </Checkbox>
                  ))}
                </React.Fragment>
              );
            });
          }}
        </Form.WithFieldValue>
      </div>
    );
  }, [eventDisciplines, onDisciplineCheck, isEventFetching]);

  return (
    <>
      <Typography.Title level={3}>Related content</Typography.Title>
      <ContentModulePicker onSubmit={handleSubmit} contentModules={relatedContents} />

      <RelatedDisciplinesCheckboxes />

      {relatedContentFields.map(({ relatedContent, ...field }) => {
        const { renderItem, label } = relatedContent;

        return (
          <Form.WithFieldValue key={field.name} name={field.name}>
            {(relatedContentItems) => {
              if (!hasArrayItems(relatedContentItems)) return null;

              return (
                <div style={{ marginTop: 20 }}>
                  <SortableArrayField
                    label={label}
                    {...field}
                    form={form}
                    titleLevel={4}
                    {...(field.type === 'relatedEvents' &&
                      onRelatedEventChange && { onRelatedEventDelete: onRelatedEventChange })}
                  >
                    {renderItem}
                  </SortableArrayField>
                </div>
              );
            }}
          </Form.WithFieldValue>
        );
      })}
    </>
  );
};

export default RelatedContentField;
